import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Info } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const SERVICES = [
  { id: "iec", label: "IEC Registration", feeLow: 1500, feeHigh: 3000, timeline: "1–2 working days", note: "DGFT govt. fee included. PAN + bank proof required.", duty: false },
  { id: "gst", label: "GST Registration", feeLow: 1000, feeHigh: 2500, timeline: "3–7 working days", note: "Includes LUT guidance for exporters.", duty: false },
  { id: "tm", label: "Trademark Filing", feeLow: 6500, feeHigh: 9000, timeline: "Filing in 48 hours", note: "Per class, incl. govt. fee for small enterprises.", duty: false },
  { id: "inc", label: "Company Incorporation", feeLow: 8000, feeHigh: 15000, timeline: "7–15 working days", note: "Pvt Ltd / LLP incl. DIN, DSC & name approval.", duty: false },
  { id: "cha", label: "CHA Customs Clearance", feeLow: 6000, feeHigh: 12000, timeline: "Per consignment", note: "Agency charges per container / shipment.", duty: true },
  { id: "ff", label: "Freight Forwarding", feeLow: 9000, feeHigh: 25000, timeline: "Lane dependent", note: "Handling & documentation. Freight quoted per lane.", duty: true },
];

const inr = (n) =>
  "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n));

export default function Estimator({ onRequestQuote }) {
  const [serviceId, setServiceId] = useState("cha");
  const [cargoValue, setCargoValue] = useState(500000);

  const svc = SERVICES.find((s) => s.id === serviceId);

  const duty = useMemo(() => {
    if (!svc.duty) return null;
    const bcd = cargoValue * 0.1;
    const sws = bcd * 0.1;
    const igst = (cargoValue + bcd + sws) * 0.18;
    return { bcd, sws, igst, total: bcd + sws + igst };
  }, [svc, cargoValue]);

  return (
    <section id="estimator" data-testid="estimator-section" className="relative py-24 sm:py-32 bg-[#111726] border-y border-white/[0.06] noise-overlay">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-amber-400 mb-4">
            Tariff Desk — Instant Estimate
          </p>
          <h2 className="font-display font-bold tracking-tight uppercase text-2xl sm:text-3xl lg:text-5xl text-slate-100 leading-tight">
            Know your cost <span className="outline-text">before</span> you ship.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-md">
            Pick a service, set your cargo value, and get an indicative duty and
            professional-fee range in seconds. Formal quotes follow within 24 hours.
          </p>
          <div className="mt-8 flex items-start gap-3 border border-white/10 rounded-md p-4 bg-[#0A0E17]/60 max-w-md">
            <Info size={16} className="text-[#F59E0B] mt-0.5 shrink-0" />
            <p className="font-mono2 text-[11px] leading-relaxed text-slate-400 uppercase tracking-wider">
              Indicative only — final duty depends on HS classification, notifications &amp; assessments.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="bg-[#161F33] border border-white/[0.08] rounded-md p-6 sm:p-8"
        >
          <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
            Select service
          </label>
          <select
            data-testid="calculator-select-service"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            className="mt-2 w-full bg-[#0A0E17] border border-white/15 rounded-sm px-4 py-3 text-sm text-slate-100 focus:border-[#F59E0B] focus:outline-none transition-colors duration-300"
          >
            {SERVICES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>

          {svc.duty && (
            <div className="mt-6">
              <div className="flex justify-between items-baseline">
                <label className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  Assessable cargo value
                </label>
                <span data-testid="calculator-cargo-value" className="font-mono2 text-sm text-[#F59E0B] font-semibold">
                  {inr(cargoValue)}
                </span>
              </div>
              <input
                data-testid="calculator-input-value"
                type="range"
                min="100000"
                max="10000000"
                step="50000"
                value={cargoValue}
                onChange={(e) => setCargoValue(Number(e.target.value))}
                className="mt-3 w-full accent-[#F59E0B]"
              />
              <div className="flex justify-between font-mono2 text-[10px] text-slate-500 mt-1">
                <span>₹1 LAKH</span>
                <span>₹1 CRORE</span>
              </div>
            </div>
          )}

          <div data-testid="calculator-result-card" className="mt-8 border-t border-white/10 pt-6 space-y-3">
            {duty && (
              <>
                <div className="flex justify-between font-mono2 text-xs text-slate-400">
                  <span>Basic Customs Duty (10%)</span><span>{inr(duty.bcd)}</span>
                </div>
                <div className="flex justify-between font-mono2 text-xs text-slate-400">
                  <span>Social Welfare Surcharge (10% of BCD)</span><span>{inr(duty.sws)}</span>
                </div>
                <div className="flex justify-between font-mono2 text-xs text-slate-400">
                  <span>IGST (18%)</span><span>{inr(duty.igst)}</span>
                </div>
                <div className="flex justify-between font-mono2 text-sm font-semibold text-white border-t border-white/10 pt-3">
                  <span>Indicative duty outgo</span>
                  <span className="text-[#F59E0B]">{inr(duty.total)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between font-mono2 text-sm text-slate-300 pt-1">
              <span>Professional fee range</span>
              <span className="text-white font-semibold">{inr(svc.feeLow)} – {inr(svc.feeHigh)}</span>
            </div>
            <div className="flex justify-between font-mono2 text-xs text-slate-400">
              <span>Typical timeline</span><span>{svc.timeline}</span>
            </div>
            <p className="font-mono2 text-[10px] text-slate-500 uppercase tracking-wider pt-1">{svc.note}</p>
          </div>

          <button
            data-testid="calculator-quote-button"
            onClick={() => onRequestQuote(svc.label)}
            className="mt-8 w-full flex items-center justify-center gap-2 bg-[#F59E0B] text-[#0A0E17] font-display font-bold uppercase tracking-wide text-sm px-6 py-4 rounded-full hover:bg-white transition-colors duration-300"
          >
            Request formal quote <ArrowUpRight size={17} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
